import { useCallback, useEffect, useRef, useState } from 'react';

import * as styled from '../styles/photo-capture';

type Props = {
  onCaptureComplete: (file: File) => void;
  onCancel: () => void;
};

const JPEG_QUALITY = 0.85;

export const PhotoCapture = ({ onCaptureComplete, onCancel }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [state, setState] = useState<Review.PhotoCaptureState>('idle');
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>(
    'environment',
  );

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(
    (mode: 'environment' | 'user') => {
      stopCamera();
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: mode },
          audio: false,
        })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => {});
          }
          setState('previewing');
        })
        .catch(() => {
          setError('Could not access camera. Please check permissions.');
        });
    },
    [stopCamera],
  );

  useEffect(() => {
    startCamera(facingMode);
    return () => stopCamera();
  }, [facingMode, startCamera, stopCamera]);

  const handleFlip = () => {
    setFacingMode((currentMode) =>
      currentMode === 'environment' ? 'user' : 'environment',
    );
  };

  const handleRetake = () => {
    setCapturedUrl(null);
    setState('previewing');
  };

  const handleConfirm = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        stopCamera();
        onCaptureComplete(file);
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  };

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) return;
    if (facingMode === 'user') {
      canvasContext.translate(canvas.width, 0);
      canvasContext.scale(-1, 1);
    }
    canvasContext.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setCapturedUrl(url);
        setState('captured');
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  };

  if (error) {
    return (
      <styled.Container>
        <styled.ErrorText>{error}</styled.ErrorText>
        <styled.Row>
          <styled.SecondaryBtn type="button" onClick={onCancel}>
            Cancel
          </styled.SecondaryBtn>
        </styled.Row>
      </styled.Container>
    );
  }

  return (
    <styled.Container>
      {state === 'captured' && capturedUrl ? (
        <>
          <styled.Preview src={capturedUrl} alt="Captured" />
          <styled.Row>
            <styled.SecondaryBtn type="button" onClick={handleRetake}>
              Retake
            </styled.SecondaryBtn>
            <styled.PrimaryBtn type="button" onClick={handleConfirm}>
              Use photo
            </styled.PrimaryBtn>
          </styled.Row>
        </>
      ) : (
        <>
          <styled.VideoWrap>
            <styled.Video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              $mirrored={facingMode === 'user'}
            />
            <styled.FlipBtn
              type="button"
              onClick={handleFlip}
              title="Flip camera"
            >
              🔄
            </styled.FlipBtn>
          </styled.VideoWrap>
          <styled.Row>
            <styled.SecondaryBtn type="button" onClick={onCancel}>
              Cancel
            </styled.SecondaryBtn>
            <styled.PrimaryBtn
              type="button"
              onClick={handleCapture}
              disabled={state !== 'previewing'}
            >
              Take photo
            </styled.PrimaryBtn>
          </styled.Row>
        </>
      )}
      <styled.HiddenCanvas ref={canvasRef} />
    </styled.Container>
  );
};
